import { useEffect, useState } from 'react';
import { MAX_ROOM_CAPACITY, ROOM_TYPES, type Room } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableSelect } from '../common/SearchableSelect';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useCreateRoom, useUpdateRoom } from '../../hooks/useFloorPlans';

interface RoomFormModalProps {
  open: boolean;
  onClose: () => void;
  /** The room being edited; null creates a new one. */
  room: Room | null;
  /** Venue a new room starts in. */
  defaultVenueId?: string | null;
  /** After a create (`created`) or an edit, with the stored room. */
  onSaved: (room: Room, created: boolean) => void;
  /** The venue is set by where the room is being added (a venue's room list), so it is not asked for. */
  venueLocked?: boolean;
}

interface RoomForm {
  name: string;
  roomType: string;
  widthFt: string;
  heightFt: string;
  maxCapacity: string;
  cocktailCapacity: string;
  venueId: string;
}

function formFor(room: Room | null, venueId?: string | null): RoomForm {
  if (!room) {
    return {
      name: '',
      roomType: '',
      widthFt: '60',
      heightFt: '40',
      maxCapacity: '',
      cocktailCapacity: '',
      venueId: venueId ?? '',
    };
  }
  return {
    name: room.name,
    roomType: room.roomType || '',
    widthFt: String(room.widthFt),
    heightFt: String(room.heightFt),
    maxCapacity: room.maxCapacity?.toString() || '',
    cocktailCapacity: room.cocktailCapacity?.toString() || '',
    venueId: room.venueId || '',
  };
}

/**
 * A typed capacity: blank is none, a whole number of guests up to
 * `MAX_ROOM_CAPACITY` is kept, and anything else is refused (`false`) rather
 * than rounded — or sent for the server to refuse with no word on why.
 */
function readCapacity(typed: string): number | undefined | false {
  if (typed.trim() === '') return undefined;
  const n = Number(typed);
  return Number.isInteger(n) && n >= 1 && n <= MAX_ROOM_CAPACITY ? n : false;
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

/**
 * Create or edit a venue room's details: name, type, size and its capacity —
 * seated at tables (banquet style) and standing (cocktail style).
 * Its shape and fixtures are the room's too, and are drawn in the room editor
 * (`RoomShapeEditorModal`) — both live on the venue's Rooms screen.
 */
export function RoomFormModal({
  open,
  onClose,
  room,
  defaultVenueId,
  onSaved,
  venueLocked = false,
}: RoomFormModalProps) {
  const [form, setForm] = useState<RoomForm>(() => formFor(room, defaultVenueId));
  const [error, setError] = useState<string | null>(null);
  const createRoom = useCreateRoom();
  const updateRoom = useUpdateRoom();

  useEffect(() => {
    if (open) {
      setForm(formFor(room, defaultVenueId));
      setError(null);
    }
  }, [open, room, defaultVenueId]);

  const set = (patch: Partial<RoomForm>) => setForm((f) => ({ ...f, ...patch }));
  const saving = createRoom.isPending || updateRoom.isPending;
  const canSave = !!form.name.trim() && (!!room || !!form.venueId) && !saving;

  const onError = () => setError('The room could not be saved. Try again.');

  const handleSave = () => {
    if (!canSave) return;
    setError(null);
    // What is typed is what is stored, or nothing is: an invalid size is
    // refused rather than quietly replaced.
    const widthFt = Number(form.widthFt);
    const heightFt = Number(form.heightFt);
    if (!Number.isFinite(widthFt) || widthFt <= 0 || !Number.isFinite(heightFt) || heightFt <= 0) {
      setError('Width and height must be more than 0 feet.');
      return;
    }
    const maxCapacity = readCapacity(form.maxCapacity);
    const cocktailCapacity = readCapacity(form.cocktailCapacity);
    if (maxCapacity === false || cocktailCapacity === false) {
      setError(
        `Capacity must be a whole number of guests, from 1 to ${MAX_ROOM_CAPACITY.toLocaleString('en-US')}.`,
      );
      return;
    }
    if (room) {
      updateRoom.mutate(
        {
          id: room.id,
          updates: {
            name: form.name,
            roomType: form.roomType || null,
            widthFt,
            heightFt,
            maxCapacity: maxCapacity ?? null,
            cocktailCapacity: cocktailCapacity ?? null,
          },
        },
        { onSuccess: (saved) => onSaved(saved ?? { ...room, widthFt, heightFt }, false), onError },
      );
      return;
    }
    // A room always belongs to a venue.
    createRoom.mutate(
      {
        name: form.name,
        roomType: form.roomType || undefined,
        widthFt,
        heightFt,
        maxCapacity,
        cocktailCapacity,
        venueId: form.venueId,
      },
      { onSuccess: (created) => onSaved(created, true), onError },
    );
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={room ? 'Edit Room' : 'New Room'}
      size="sm"
      // A save on its way finishes what it started (a new room goes on to its
      // shape), so the form cannot be closed from under it.
      closeOnBackdrop={!saving}
      closeOnEscape={!saving}
      showCloseButton={!saving}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50"
            data-testid="room-form-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
            data-testid="room-form-save"
          >
            {room ? 'Save Changes' : 'Create Room'}
          </button>
        </div>
      }
    >
      <div className="space-y-3" data-testid="room-modal">
        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <div>
          <label className={labelClass} htmlFor="room-form-name">
            Name
          </label>
          <input
            id="room-form-name"
            type="text"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            className={inputClass}
            data-testid="room-form-name"
          />
        </div>
        <div>
          <span className={labelClass}>Room Type</span>
          <SearchableListSelect<string>
            items={[...ROOM_TYPES]}
            value={form.roomType || null}
            onChange={(v) => set({ roomType: typeof v === 'string' ? v : '' })}
            getValue={(t) => t}
            getLabel={(t) => t}
            placeholder="Select type..."
            ariaLabel="Room type"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass} htmlFor="room-form-width">
              Width (ft)
            </label>
            <input
              id="room-form-width"
              type="number"
              value={form.widthFt}
              onChange={(e) => set({ widthFt: e.target.value })}
              min="1"
              className={inputClass}
              data-testid="room-form-width"
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="room-form-height">
              Height (ft)
            </label>
            <input
              id="room-form-height"
              type="number"
              value={form.heightFt}
              onChange={(e) => set({ heightFt: e.target.value })}
              min="1"
              className={inputClass}
              data-testid="room-form-height"
            />
          </div>
        </div>
        {room?.shape && (
          <p className="text-xs text-gray-500" data-testid="room-form-shape-note">
            This room has a drawn shape. A new width or height stretches it to fit; to change a
            single wall, use Shape &amp; fixtures.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass} htmlFor="room-form-capacity">
              Banquet capacity
            </label>
            <input
              id="room-form-capacity"
              type="number"
              value={form.maxCapacity}
              onChange={(e) => set({ maxCapacity: e.target.value })}
              min="1"
              max={MAX_ROOM_CAPACITY}
              placeholder="Optional"
              className={inputClass}
              data-testid="room-form-capacity"
            />
            <p className="mt-1 text-xs text-gray-500">Seated at tables</p>
          </div>
          <div>
            <label className={labelClass} htmlFor="room-form-cocktail-capacity">
              Cocktail capacity
            </label>
            <input
              id="room-form-cocktail-capacity"
              type="number"
              value={form.cocktailCapacity}
              onChange={(e) => set({ cocktailCapacity: e.target.value })}
              min="1"
              max={MAX_ROOM_CAPACITY}
              placeholder="Optional"
              className={inputClass}
              data-testid="room-form-cocktail-capacity"
            />
            <p className="mt-1 text-xs text-gray-500">Standing reception</p>
          </div>
        </div>
        {!room && !venueLocked && (
          <div>
            <label className={labelClass}>
              Venue <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              entityType="venue"
              value={form.venueId}
              onChange={(v) => set({ venueId: typeof v === 'string' ? v : '' })}
              placeholder="Search venue..."
            />
            {!form.venueId && (
              <p className="mt-1 text-xs text-amber-600" data-testid="room-venue-required">
                A room must belong to a venue.
              </p>
            )}
          </div>
        )}
      </div>
    </EntityModal>
  );
}
